import { getUserId } from '@/utils/auth';
import { gql, useMutation, useQuery } from '@apollo/client';
import { useCallback, useEffect, useRef, useState } from 'react';

const GET_NOTIFICATIONS = gql`
   query GetNotifications {
      getNotifications {
         id
         type
         title
         message
         read
         createdAt
         metadata {
            applicationId
            stage
            status
            remarks
         }
      }
   }
`;

const GET_UNREAD_NOTIFICATIONS = gql`
   query GetUnreadNotifications {
      getUnreadNotifications {
         id
         type
         title
         message
         read
         createdAt
         metadata {
            applicationId
            stage
            status
            remarks
         }
      }
   }
`;

const MARK_NOTIFICATION_READ = gql`
   mutation MarkNotificationAsRead($id: ID!) {
      markNotificationAsRead(id: $id) {
         id
         read
      }
   }
`;

const MARK_ALL_NOTIFICATIONS_READ = gql`
   mutation MarkAllNotificationsAsRead {
      markAllNotificationsAsRead {
         success
      }
   }
`;

const GET_UNREAD_COUNT = gql`
   query GetUnreadNotificationsCount {
      getUnreadNotificationsCount
   }
`;

export const useUserNotifications = () => {
   const userId = getUserId();
   const [displayedNotifications, setDisplayedNotifications] = useState([]);
   const [displayedUnreadNotifications, setDisplayedUnreadNotifications] = useState([]);
   const [hasMore, setHasMore] = useState(true);
   const [hasMoreUnread, setHasMoreUnread] = useState(true);
   const ITEMS_PER_PAGE = 15;
   const currentIndexRef = useRef(0);
   const unreadIndexRef = useRef(0);

   const { data: allData, loading: allLoading, error: allError, refetch: refetchAll } = useQuery(GET_NOTIFICATIONS, {
      fetchPolicy: 'network-only',
      notifyOnNetworkStatusChange: true,
      pollInterval: 30000
   });

   const { data: unreadData, loading: unreadLoading, refetch: refetchUnread } = useQuery(GET_UNREAD_NOTIFICATIONS, {
      fetchPolicy: 'network-only',
      notifyOnNetworkStatusChange: true,
      pollInterval: 30000
   });

   const [markAsReadMutation] = useMutation(MARK_NOTIFICATION_READ);
   const [markAllAsReadMutation] = useMutation(MARK_ALL_NOTIFICATIONS_READ);

   const allNotifications = allData?.getNotifications || [];
   const unreadNotifications = unreadData?.getUnreadNotifications || [];

   const { data: countData, refetch: refetchCount } = useQuery(GET_UNREAD_COUNT, {
      pollInterval: 30000
   });

   // Handle all notifications
   useEffect(() => {
      if (!allLoading && allNotifications.length >= 0) {
         setDisplayedNotifications(allNotifications.slice(0, ITEMS_PER_PAGE));
         setHasMore(allNotifications.length > ITEMS_PER_PAGE);
         currentIndexRef.current = ITEMS_PER_PAGE;
      }
   }, [allNotifications, allLoading]);

   // Handle unread notifications
   useEffect(() => {
      if (!unreadLoading && unreadNotifications.length >= 0) {
         setDisplayedUnreadNotifications(unreadNotifications.slice(0, ITEMS_PER_PAGE));
         setHasMoreUnread(unreadNotifications.length > ITEMS_PER_PAGE);
         unreadIndexRef.current = ITEMS_PER_PAGE;
      }
   }, [unreadNotifications, unreadLoading]);

   const loadMore = useCallback(() => {
      if (!hasMore || allLoading) return;

      const nextIndex = currentIndexRef.current + ITEMS_PER_PAGE;
      const newNotifications = allNotifications.slice(0, nextIndex);

      setDisplayedNotifications(newNotifications);
      setHasMore(nextIndex < allNotifications.length);
      currentIndexRef.current = nextIndex;
   }, [hasMore, allLoading, allNotifications]);

   const loadMoreUnread = useCallback(() => {
      if (!hasMoreUnread || unreadLoading) return;

      const nextIndex = unreadIndexRef.current + ITEMS_PER_PAGE;
      const newNotifications = unreadNotifications.slice(0, nextIndex);

      setDisplayedUnreadNotifications(newNotifications);
      setHasMoreUnread(nextIndex < unreadNotifications.length);
      unreadIndexRef.current = nextIndex;
   }, [hasMoreUnread, unreadLoading, unreadNotifications]);

   const markAsRead = useCallback(async (id) => {
      try {
         await markAsReadMutation({
            variables: { id }
         });
         await Promise.all([refetchAll(), refetchUnread()]);
      } catch (error) {
         console.error('Error marking notification as read:', error);
      }
   }, [markAsReadMutation, refetchAll, refetchUnread]);

   const markAllAsRead = useCallback(async () => {
      try {
         await markAllAsReadMutation();
         await Promise.all([refetchAll(), refetchUnread()]);
      } catch (error) {
         console.error('Error marking all notifications as read:', error);
      }
   }, [markAllAsReadMutation, refetchAll, refetchUnread]);

   const isLoading = allLoading && !displayedNotifications.length;
   const isUnreadLoading = unreadLoading && !displayedUnreadNotifications.length;

   return {
      notifications: displayedNotifications,
      unreadNotifications: displayedUnreadNotifications,
      allUnreadCount: unreadNotifications.length,
      loading: isLoading,
      unreadLoading: isUnreadLoading,
      error: allError,
      markAsRead,
      markAllAsRead,
      hasMore,
      hasMoreUnread,
      loadMore,
      loadMoreUnread,
      unreadCount: countData?.getUnreadNotificationsCount || 0,
   };
};
